import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Shield, FileText, Cookie, Eye, ChevronRight } from 'lucide-react';

const LEGAL_PAGES = [
  {
    title: 'Privacy Policy',
    description: 'How we collect, use, and protect your personal data under UK GDPR and the Data Protection Act 2018.',
    url: '/privacy-policy',
    icon: Shield,
    accent: '#20acf6',
  },
  {
    title: 'Terms of Service',
    description: 'The terms and conditions governing your use of the Common Impact E-Learning platform.',
    url: '/terms-of-service',
    icon: FileText,
    accent: '#02d1d4',
  },
  {
    title: 'Cookies Policy',
    description: 'What cookies we use, why we use them, and how you can manage your cookie preferences.',
    url: '/cookies-policy',
    icon: Cookie,
    accent: '#F59E0B',
  },
  {
    title: 'Accessibility Statement',
    description: 'Our commitment to making e-learning accessible to all learners, including those with disabilities.',
    url: '/accessibility',
    icon: Eye,
    accent: '#8B5CF6',
  },
];

const LegalPage = () => {
  return (
    <div className="min-h-screen flex flex-col bg-[#F8FAFC]" data-testid="legal-page">
      <Helmet>
        <title>Legal | Common Impact E-Learning</title>
        <meta name="description" content="Legal information for the Common Impact E-Learning platform including privacy policy, terms of service, cookies policy, and accessibility statement." />
      </Helmet>
      <Header />

      <section className="bg-[#2a3e5d] text-white py-14">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-3xl sm:text-4xl font-bold mb-3">Legal Information</h1>
          <p className="text-white/70 text-base max-w-xl mx-auto">
            Transparency matters. Below you will find all our legal documents, policies, and commitments to your rights as a user of Common Impact E-Learning.
          </p>
        </div>
      </section>

      <section className="py-12 flex-1">
        <div className="max-w-4xl mx-auto px-4 grid sm:grid-cols-2 gap-5">
          {LEGAL_PAGES.map((page) => (
            <Link key={page.url} to={page.url} className="block group" data-testid={`legal-link-${page.url.slice(1)}`}>
              <Card className="h-full border-2 border-slate-200 hover:border-opacity-60 hover:shadow-lg transition-all duration-200" style={{ '--hover-border': page.accent }}>
                <CardContent className="p-6 flex flex-col h-full">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: `${page.accent}15` }}>
                    <page.icon className="h-5 w-5" style={{ color: page.accent }} />
                  </div>
                  <h2 className="text-lg font-semibold text-[#2a3e5d] mb-2 group-hover:text-[#20acf6] transition-colors">
                    {page.title}
                  </h2>
                  <p className="text-sm text-slate-500 flex-1 mb-3">{page.description}</p>
                  <span className="text-xs font-medium text-[#20acf6] flex items-center gap-1 group-hover:gap-2 transition-all">
                    Read full document <ChevronRight className="h-3.5 w-3.5" />
                  </span>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        <div className="max-w-4xl mx-auto px-4 mt-10">
          <div className="bg-white rounded-xl border border-slate-200 p-6 text-center">
            <p className="text-sm text-slate-600">
              If you have questions about any of our policies, please{' '}
              <Link to="/contact" className="text-[#20acf6] hover:underline font-medium">contact us</Link>.
            </p>
            <p className="text-xs text-slate-400 mt-2">
              Common Impact E-Learning Ltd | Registered in England & Wales | 71-75 Shelton Street, London, WC2H 9JQ, England
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LegalPage;
